import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle, CheckCircle } from 'lucide-react';

export default function QuestionnaireSection() {
  const [showTypeform, setShowTypeform] = useState(false);

  return (
    <section className="relative py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute -bottom-24 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-[100px]" />
      </div>
      
      {/* Top border with gradient effect */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-4"
          >
            <span className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 text-primary px-6 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border border-primary/10">
              Self-Assessment
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            Are You Ready to Receive <span className="text-primary">Feedback?</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Take our quick 7-question assessment to gauge your readiness for meaningful feedback
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Take This Quick Assessment?</h3>
                <ul className="space-y-4">
                  {[
                    'Understand your feedback readiness',
                    'Reflect on possible steps prior to the process',
                    'Prepare mentally for constructive criticism',
                    'Maximize the value of your feedback experience'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What You'll Discover</h3>
                <p className="text-gray-700 mb-6">
                  This quick assessment helps you evaluate your mindset and emotional readiness to receive and act on feedback effectively.
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowTypeform(true)}
                    className="bg-gradient-to-r from-primary to-secondary text-dark px-8 py-4 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 font-medium"
                  >
                    Take the Assessment
                  </button>
                </div>
              </div>
            </div>
            
            <div className="text-center text-gray-500 text-sm">
              <p>Takes approximately 3 minutes to complete. No registration required</p>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {showTypeform && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative w-full max-w-4xl h-[80vh] bg-white rounded-xl shadow-2xl"
              >
                <button
                  onClick={() => setShowTypeform(false)}
                  className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
                
                <iframe
                  className="typeform-widget w-full h-full rounded-xl"
                  src="https://insightalks.typeform.com/to/krtyccr0"
                  frameBorder="0"
                  title="Readiness Questionnaire"
                  allow="camera; microphone; autoplay; encrypted-media; fullscreen"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom border with gradient effect */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
}